// CollapsibleCell.jsx
import React, { useState, useMemo, useRef, useEffect } from "react";
import "./CollapsibleCell.css";

/**
 * CollapsibleCell
 * - Shows up to 2 lines of content
 * - Expands/collapses on click
 * - Works with strings, numbers, arrays, objects
 * - Optional `render` prop lets you format the text (eg. bold highlighting)
 */
const CollapsibleCell = ({ data, render, className = "" }) => {
  const [expanded, setExpanded] = useState(false);
  const [isOverflow, setIsOverflow] = useState(false);
  const contentRef = useRef(null);

  // Normalize any kind of input -> plain string for measurement
  const textString = useMemo(() => {
    if (data == null) return "";
    if (typeof data === "string") return data;
    if (typeof data === "number") return String(data);
    if (Array.isArray(data)) return data.join(", ");
    if (typeof data === "object") {
      if ("value" in data) return String(data.value);
      if ("text" in data) return String(data.text);

      const preferKeys = ["description", "summary", "reasoning"];
      for (const k of preferKeys) {
        if (k in data) return String(data[k]);
      }

      try {
        return JSON.stringify(data);
      } catch {
        return String(data);
      }
    }
    return String(data);
  }, [data]);

  // After mount/update, check if content overflows 2 lines
  useEffect(() => {
    const el = contentRef.current;
    if (!el) {
      setIsOverflow(false);
      return;
    }
    requestAnimationFrame(() => {
      const over = el.scrollHeight > el.clientHeight + 1;
      setIsOverflow(over);
    });
  }, [textString]);

  // If a render function is supplied, use it
  const contentNode = render ? render(textString) : textString || "—";

  const handleKeyDown = (e) => {
    if (!isOverflow) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setExpanded((p) => !p);
    }
  };

  return (
    <div className={`collapsible-cell-wrapper ${className}`}>
      <div
        ref={contentRef}
        className={`collapsible-cell ${expanded ? "expanded" : ""} ${
          isOverflow ? "is-overflow" : ""
        }`}
        onClick={() => isOverflow && setExpanded((p) => !p)}
        role={isOverflow ? "button" : undefined}
        tabIndex={isOverflow ? 0 : -1}
        onKeyDown={handleKeyDown}
        aria-expanded={isOverflow ? expanded : undefined}
        title="Click to expand/shrink"
      >
        {contentNode}
      </div>

      {/* {isOverflow && (
        <button
          type="button"
          className="collapsible-toggle"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((p) => !p);
          }}
          aria-label={expanded ? "Show less" : "Show more"}
        >
          {expanded ? "Show less ▲" : "Show more ▼"}
        </button>
      )} */}
    </div>
  );
};

export default CollapsibleCell;
